import '../../styles/error-summary.scss';

import { forwardRef, ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ErrorSummaryProps {
  heading?: string;
  description?: ReactNode;
  errors?: {
    targetName?: string;
    text?: string;
  }[];
  onHandleErrorClick?: (targetName: string) => void;
  pinToTop?: boolean;
}

const ErrorSummary = forwardRef<HTMLDivElement, ErrorSummaryProps>((props: ErrorSummaryProps, ref) => {
  const { heading = 'There is a problem', description, errors = [], onHandleErrorClick, pinToTop = true } = props;
  const topNotificationPortal = document.getElementById('topNotificationPortal');
  const defaultRef = useRef<HTMLDivElement>(null);
  const innerRef = ref || defaultRef;

  useEffect(() => {
    if (innerRef && 'current' in innerRef && innerRef.current) {
      innerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [innerRef]);

  const errorSummary = (
    <div className="govuk-error-summary" data-module="govuk-error-summary" ref={innerRef}>
      <div role="alert">
        <h2 className="govuk-error-summary__title">{heading}</h2>
        <div className="govuk-error-summary__body">
          {description}
          {errors.length > 0 && (
            <ul className="govuk-list govuk-error-summary__list">
              {errors.map((error, index) => (
                <li key={index}>
                  <a
                    href="#"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      onHandleErrorClick?.(error.targetName ?? '');
                    }}
                  >
                    {error.text}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return <>{pinToTop && topNotificationPortal ? createPortal(errorSummary, topNotificationPortal) : errorSummary}</>;
});
ErrorSummary.displayName = 'ErrorSummary';
export default ErrorSummary;
